import React, { useState } from 'react';
import { Row, Col, Card, Badge, Alert, Button } from 'react-bootstrap';
import type { Customer } from '../api/generated/model';
import CustomerSourceCard from './CustomerSourceCard';
import CustomerEditModal from './CustomerEditModal';
import SlingLocationMappingsModal from './SlingLocationMappingsModal';

function formatDateTime(dateTime: string | undefined): string {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${month}/${day}/${year} ${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

interface CustomerDetailContentProps {
  customer: Customer;
  // Fired when the customer is mutated (source deleted, edited, etc.) so the
  // parent can refetch.
  onRefresh?: () => void;
}

const CustomerDetailContent: React.FC<CustomerDetailContentProps> = ({ customer, onRefresh }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSlingMappingsModal, setShowSlingMappingsModal] = useState(false);

  const handleSourceDelete = () => {
    if (onRefresh) onRefresh();
  };

  const canEdit = customer.primary_source?.source_type === 'internal';

  return (
    <>
      <style>{`
        .source-card {
          transition: box-shadow 0.15s ease-in-out;
        }
        .source-card:hover {
          box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.15) !important;
        }
      `}</style>

      {/* Header Card */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h2 className="mb-3">{customer.name}</h2>
                  <div className="mb-2">
                    <strong>Customer ID:</strong> <code className="text-muted ms-2">{customer.id}</code>
                  </div>
                  <div className="mb-2">
                    <strong>Created:</strong> <span className="text-muted ms-2">{formatDateTime(customer.created_at)}</span>
                  </div>
                  <div className="mb-2">
                    <strong>Updated:</strong> <span className="text-muted ms-2">{formatDateTime(customer.updated_at)}</span>
                  </div>
                  {customer.conflicts && customer.conflicts.length > 0 && (
                    <div className="mt-3">
                      <Badge bg="warning" text="dark" className="fs-6">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {customer.conflicts.length} Active Conflict{customer.conflicts.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    disabled={!canEdit}
                    onClick={() => setShowEditModal(true)}
                    title={!canEdit ? 'Only internal customers can be edited' : ''}
                  >
                    <i className="bi bi-pencil me-2"></i>
                    Edit
                  </Button>
                </div>
              </div>

              {/* Locations section */}
              <div className="border-top pt-3">
                <div className="d-flex align-items-center mb-2">
                  <strong>Locations:</strong>
                  <Button
                    variant="primary"
                    size="sm"
                    className="ms-2"
                    onClick={() => setShowSlingMappingsModal(true)}
                  >
                    <i className="bi bi-pencil me-2"></i>
                    Edit
                  </Button>
                </div>
                {customer.locations && customer.locations.length > 0 ? (
                  <table className="table table-sm table-striped table-bordered" style={{ width: 'auto' }}>
                    <thead>
                      <tr>
                        <th>Customer Location</th>
                        <th>Sling Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customer.locations.map((mapping, idx) => (
                        <tr key={mapping.id || `unmapped-${idx}`}>
                          <td>
                            {mapping.location ? mapping.location : <Badge bg="secondary">Primary/Default</Badge>}
                          </td>
                          <td>
                            {mapping.sling_location_id ? (
                              <a
                                href={`https://app.getsling.com/manage/locations/${mapping.sling_location_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {mapping.sling_location?.name || 'Unknown'} ({mapping.sling_location?.state || 'N/A'})
                              </a>
                            ) : (
                              <span className="text-muted fst-italic">None</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-muted small ms-2">No locations mapped</div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sources Section */}
      <Row className="mb-4">
        <Col>
          <h4 className="mb-3">
            <i className="bi bi-diagram-3 me-2"></i>
            Sources
          </h4>
          {customer.all_sources && customer.all_sources.length > 0 ? (
            <div className="d-flex flex-column gap-3">
              {customer.all_sources.map((source) => (
                <CustomerSourceCard
                  key={source.id}
                  source={source}
                  isPrimary={source.id === customer.primary_source_id}
                  onDelete={handleSourceDelete}
                  customerId={customer.id}
                />
              ))}
            </div>
          ) : (
            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              No sources found for this customer.
            </Alert>
          )}
        </Col>
      </Row>

      {/* Conflicts Section */}
      {customer.conflicts && customer.conflicts.length > 0 && (
        <Row className="mb-4">
          <Col>
            <h4 className="mb-3">
              <i className="bi bi-exclamation-triangle text-warning me-2"></i>
              Conflicts ({customer.conflicts.length})
            </h4>
            {customer.conflicts.map((conflict) => (
              <Card key={conflict.id} className="border-warning mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-2">
                        <Badge bg="warning" text="dark" className="me-2">
                          {conflict.status}
                        </Badge>
                        {conflict.field_path}
                      </h6>
                      <div className="small text-muted mb-2">
                        <strong>Conflict ID:</strong> <code>{conflict.id}</code>
                      </div>
                      <div className="small text-muted">
                        <strong>Created:</strong> {formatDateTime(conflict.created_at)}
                      </div>
                    </div>
                  </div>
                  <hr className="my-3" />
                  <Row>
                    <Col md={6}>
                      <div className="small">
                        <strong className="text-muted">Source A:</strong>
                        <div className="mt-2">
                          {conflict.a_source ? (
                            <CustomerSourceCard source={conflict.a_source} isPrimary={false} compact />
                          ) : (
                            <code className="text-muted">{conflict.a_source_id}</code>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="small">
                        <strong className="text-muted">Source B:</strong>
                        <div className="mt-2">
                          {conflict.b_source ? (
                            <CustomerSourceCard source={conflict.b_source} isPrimary={false} compact />
                          ) : (
                            <code className="text-muted">{conflict.b_source_id}</code>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </Col>
        </Row>
      )}

      <CustomerEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        customer={customer}
      />

      <SlingLocationMappingsModal
        show={showSlingMappingsModal}
        onHide={() => setShowSlingMappingsModal(false)}
        customerId={customer.id || ''}
        customerName={customer.name || ''}
      />
    </>
  );
};

export default CustomerDetailContent;
